import _styled, { default as _styled2 } from "styled-components";
import _merge from "lodash/merge";
import React, { useEffect, useMemo } from 'react';
import { EmptyState, Flex, Spinner } from 'core/components';
import { observer } from 'mobx-react';
import UdeLegend from 'app/views/boards/panels/ude/UdeLegend';
import { HighchartsVizTypes, TimeSeriesVizTypes } from 'app/views/ude/dataviews/util/highcharts';
import storeLoader from 'app/stores/storeLoader';
import { useDictionaryMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import { dataViewTypes } from 'app/stores/$dataviews';
import { getEnumHealthyValue, isEnumItem } from '@kentik/ui-shared/query/enumUtils';
import HighchartsDataView from './dataviews/HighchartsDataView';
import UdeDataViewSkeleton from './UdeDataViewSkeleton';
import { useDragToZoom } from './useDragToZoom';
import { ZoomSelectionPopover } from './components/ZoomSelectionPopover';
import { ZoomSelectionOverlay } from './components/ZoomSelectionOverlay';
const noop = () => {};
function getPlaceholderProps(queryModel, measurementState) {
  if (queryModel.hasError || measurementState.isError) {
    return {
      title: 'There was an error running this query.',
      icon: 'error'
    };
  }
  return {
    title: 'No results found.',
    icon: 'issue'
  };
}
function Chart({
  queryModel,
  measurement,
  onChartRef,
  chartOverrides
}) {
  const chartType = queryModel.settings.chart.type;

  // TODO: Implement loading/error state for measurement fetch (may come for free with React-Query)
  if (!measurement) {
    return /*#__PURE__*/React.createElement(Spinner, null);
  }
  if (chartType && HighchartsVizTypes.includes(chartType)) {
    return /*#__PURE__*/React.createElement(HighchartsDataView, {
      measurement: measurement,
      queryModel: queryModel,
      onChartRef: onChartRef,
      chartOverrides: chartOverrides
    });
  }

  // TODO other chart types
  return /*#__PURE__*/React.createElement(React.Fragment, null, "Coming Soon!");
}
function UdeDataView({
  queryModel,
  onChartRef,
  legendMaxWidth,
  chartOverrides,
  padding,
  onZoomToSelection,
  onAddRowFilters,
  isPending = false
}) {
  const {
    measurement,
    ...measurementState
  } = useDictionaryMeasurement(queryModel.query.measurement);
  const isTimeSeries = TimeSeriesVizTypes.includes(queryModel.settings.chart.type);
  const {
    chartOverrides: zoomOverrides,
    selection,
    containerRef,
    confirmZoom,
    dismissSelection
  } = useDragToZoom({
    enabled: isTimeSeries && !!onZoomToSelection,
    onZoom: onZoomToSelection ?? noop
  });

  // Merge zoom overrides with any externally supplied chartOverrides
  const mergedOverrides = useMemo(() => {
    if (!zoomOverrides && !chartOverrides) {
      return undefined;
    }
    if (!zoomOverrides) {
      return chartOverrides;
    }
    if (!chartOverrides) {
      return zoomOverrides;
    }
    return _merge({}, chartOverrides, zoomOverrides);
  }, [chartOverrides, zoomOverrides]);
  const dataView = dataViewTypes[queryModel?.settings?.chart?.type ?? 'line'];
  const hasRequirements = dataView?.requirements?.(queryModel.query);

  // Run query on mount and whenever the query model is marked dirty.
  useEffect(() => {
    if (!hasRequirements && queryModel.dirty) {
      queryModel.requestRun();
    }
  }, [hasRequirements, queryModel, queryModel.query]);
  if (hasRequirements) {
    return /*#__PURE__*/React.createElement(EmptyState, {
      icon: "warning-sign",
      title: hasRequirements.title,
      description: hasRequirements.description
    });
  }
  const isTimelineIncompatible = measurement && queryModel.settings.chart.type === 'timelineView' && !queryModel.query.metrics.some(m => {
    const metricDetails = measurement.storage?.Metrics?.[m.name];
    return isEnumItem(metricDetails) || getEnumHealthyValue(undefined, m.name) !== undefined;
  });
  const isTableOnly = queryModel.settings.chart.type === 'table';
  const legendEnabled = (isTableOnly || queryModel.settings?.chart?.legend?.enable) ?? true;
  const legendPosition = isTableOnly ? 'bottom' : queryModel.settings?.chart?.legend?.position ?? 'right';
  const directionMap = {
    right: 'row',
    left: 'row-reverse',
    bottom: 'column'
  };
  const flexDir = directionMap[legendPosition];
  const isAwaitingFirstRun = queryModel.status === 'init' && queryModel.dirty;
  const showSkeleton = isPending || isAwaitingFirstRun || queryModel.isLoading || measurementState.isLoading;
  const showEmpty = !showSkeleton && (!queryModel.hasResults || !measurement || isTimelineIncompatible);
  return /*#__PURE__*/React.createElement(Flex, {
    display: "grid",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    gridTemplateRows: "1fr",
    gridTemplateColumns: "1fr"
  }, showSkeleton && /*#__PURE__*/React.createElement(_StyledFlex, {
    width: "100%",
    height: "100%",
    bg: "cardBackground",
    gridArea: "1 / 1"
  }, /*#__PURE__*/React.createElement(UdeDataViewSkeleton, {
    queryModel: queryModel,
    legendMaxWidth: legendMaxWidth,
    padding: padding
  })), showEmpty && /*#__PURE__*/React.createElement(_StyledFlex2, {
    width: "100%",
    height: "100%",
    bg: "cardBackground",
    gridArea: "1 / 1"
  }, isTimelineIncompatible ? /*#__PURE__*/React.createElement(EmptyState, {
    icon: "timeline-bar-chart",
    title: "Timeline view is only available for status-style metrics.",
    description: "Select a metric like Operational Status, Session State, or Admin Status to use this view."
  }) : /*#__PURE__*/React.createElement(EmptyState, getPlaceholderProps(queryModel, measurementState))), /*#__PURE__*/React.createElement(Flex, {
    flexDirection: flexDir,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    alignItems: "stretch",
    gridArea: "1 / 1"
  }, measurement && queryModel.settings.chart.enable && !isTableOnly && /*#__PURE__*/React.createElement(Flex, {
    ref: containerRef,
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    key: queryModel.mainQueryRequest.requestId,
    overflow: "hidden",
    p: padding ?? 1,
    position: "relative"
  }, /*#__PURE__*/React.createElement(Chart, {
    queryModel: queryModel,
    measurement: measurement,
    onChartRef: onChartRef,
    chartOverrides: mergedOverrides
  }), selection && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ZoomSelectionOverlay, {
    x: selection.rectX,
    y: selection.rectY,
    width: selection.rectWidth,
    height: selection.rectHeight
  }), /*#__PURE__*/React.createElement(ZoomSelectionPopover, {
    x: selection.popoverX,
    y: selection.popoverY,
    onZoom: confirmZoom,
    onDismiss: dismissSelection
  }))), legendEnabled && /*#__PURE__*/React.createElement(UdeLegend, {
    queryModel: queryModel,
    position: legendPosition,
    maxWidth: legendMaxWidth,
    onAddRowFilters: onAddRowFilters
  })));
}
export default storeLoader('$metrics')(observer(UdeDataView));
var _StyledFlex = _styled(Flex)({
  zIndex: 2
});
var _StyledFlex2 = _styled2(Flex).withConfig({
  displayName: "UdeDataView___StyledFlex2",
  componentId: "sc-1xk1v02-0"
})({
  zIndex: 2
});